import React from "react";
import { DataSubjectRequestDetailDto } from "../../../../common/service/server/dto/DataSubjectRequestDto";
import { AlertCard } from "@truevault/aquilo";
import { Fmt } from "../../../formatters";
import { Organization } from "../../../../common/models";
import clsx from "clsx";

type AutomatedDsarSource = "MINE_EMAIL" | "PRIVACYHAWK_EMAIL";

const AUTOMATED_DSAR_SOURCES: Record<AutomatedDsarSource, { name: string; logoSrc: string }> = {
  MINE_EMAIL: {
    name: "Mine",
    logoSrc: "/assets/images/mine-os-logo.svg",
  },
  PRIVACYHAWK_EMAIL: {
    name: "Privacy Hawk",
    logoSrc: "/assets/images/privacy-hawk-logo.svg",
  },
};

function isAutomatedDsarSource(source: string): source is AutomatedDsarSource {
  return source === "MINE_EMAIL" || source === "PRIVACYHAWK_EMAIL";
}

export interface DataRequestCustomFieldDisplayProps {
  org: Organization;
  request: DataSubjectRequestDetailDto;
  classList?: string;
}

export const DataRequestCustomFieldDisplay = ({
  org,
  request,
  classList = "",
}: DataRequestCustomFieldDisplayProps) => {
  const isBatch = !!request.batch;
  const { customOptOutFieldEnabled, customOptOutFieldLabel, customFieldEnabled, customFieldLabel } =
    org;
  const customOptOutFieldValue = request.organizationCustomInput;
  const { workflowType: workflow } = request.dataRequestType;
  const legacyCustomFieldLabel =
    workflow == "OPT_OUT" || workflow == "LIMIT" || workflow == "PROFILING_OPT_OUT"
      ? customOptOutFieldLabel
      : customFieldLabel;
  const hasCustomFields =
    !!request.subjectCustomFieldsFmt && Object.keys(request.subjectCustomFieldsFmt).length > 0;

  const automatedSource = isAutomatedDsarSource(request.submissionSource)
    ? AUTOMATED_DSAR_SOURCES[request.submissionSource]
    : null;

  const showCustomFields =
    !isBatch && (customOptOutFieldEnabled || hasCustomFields || customFieldEnabled);

  // Show nothing if no custom fields and no automated source
  if (!showCustomFields && !automatedSource) {
    return null;
  }

  // Show just the automated source one-liner if no custom fields
  if (!showCustomFields && automatedSource) {
    return (
      <div className={clsx("tw-boundary", classList)}>
        <AlertCard variant="info">
          <div className="flex items-center">
            <span className="text-sm text-neutral-600">
              DSAR Request automatically submitted by:
            </span>
            <span className="flex items-center gap-1.5 px-2">
              <img
                src={automatedSource.logoSrc}
                alt={automatedSource.name}
                width={20}
                height={20}
                className="rounded"
              />
              <span className="text-sm font-medium text-neutral-900 pl-[8px]">
                {automatedSource.name}
              </span>
            </span>
          </div>
        </AlertCard>
      </div>
    );
  }

  return (
    <div className={clsx("tw-boundary", classList)}>
      <AlertCard variant="general">
        {automatedSource && (
          <div className="flex items-center mb-3 pb-3 border-b border-neutral-300">
            <span className="text-sm text-neutral-600">
              DSAR Request automatically submitted by:
            </span>
            <span className="flex items-center gap-1.5 px-2">
              <img
                src={automatedSource.logoSrc}
                alt={automatedSource.name}
                width={20}
                height={20}
                className="rounded"
              />
              <span className="text-sm font-medium text-neutral-900 pl-[8px]">
                {automatedSource.name}
              </span>
            </span>
          </div>
        )}
        <ul
          className="mt-0 mb-0"
          style={{
            listStyleType: "disc",
            paddingLeft: "20px",
            margin: 0,
          }}
        >
          <li>Name: {Fmt.DataSubject.fmtFullName(request)}</li>
          <li>Email: {request.subjectEmailAddress}</li>

          {(customOptOutFieldEnabled || customFieldEnabled) && customOptOutFieldValue && (
            <li>
              {legacyCustomFieldLabel}: {customOptOutFieldValue}
            </li>
          )}
          {hasCustomFields && (
            <>
              {Object.entries(request.subjectCustomFieldsFmt)
                .filter(([, value]) => value && value.trim() !== "")
                .map(([key, value]) => {
                  return (
                    <li key={key}>
                      {key}: {value}
                    </li>
                  );
                })}
            </>
          )}
        </ul>
      </AlertCard>
    </div>
  );
};
